import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
// Using native Math instead of mathjs for better compatibility

interface GradualBlurProps {
  position?: 'top' | 'bottom' | 'left' | 'right'
  strength?: number
  height?: string
  width?: string
  divCount?: number
  exponential?: boolean
  zIndex?: number
  animated?: boolean | 'scroll'
  duration?: string
  easing?: string
  opacity?: number
  curve?: 'linear' | 'bezier' | 'ease-in' | 'ease-out' | 'ease-in-out'
  responsive?: boolean
  mobileHeight?: string
  tabletHeight?: string
  desktopHeight?: string
  mobileWidth?: string
  tabletWidth?: string
  desktopWidth?: string
  preset?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'subtle'
    | 'intense'
    | 'smooth'
    | 'sharp'
    | 'header'
    | 'footer'
    | 'sidebar'
    | 'page-header'
    | 'page-footer'
  gpuOptimized?: boolean
  hoverIntensity?: number
  target?: 'parent' | 'page'
  onAnimationComplete?: () => void
  className?: string
  customStyle?: Record<string, any>
}


export default /*#__PURE__*/_defineComponent({
  __name: 'gradual-blur',
  props: {
    position: { default: 'bottom' },
    strength: { default: 2 },
    height: { default: '6rem' },
    width: null,
    divCount: { default: 5 },
    exponential: { type: Boolean, default: false },
    zIndex: { default: 1000 },
    animated: { type: [Boolean, String], default: false },
    duration: { default: '0.3s' },
    easing: { default: 'ease-out' },
    opacity: { default: 1 },
    curve: { default: 'linear' },
    responsive: { type: Boolean, default: false },
    mobileHeight: null,
    tabletHeight: null,
    desktopHeight: null,
    mobileWidth: null,
    tabletWidth: null,
    desktopWidth: null,
    preset: null,
    gpuOptimized: { type: Boolean },
    hoverIntensity: null,
    target: { default: 'parent' },
    onAnimationComplete: null,
    className: { default: '' },
    customStyle: { default: () => ({}) }
  },
  setup(__props: any) {

const props = __props as { position: 'top' | 'bottom' | 'left' | 'right', strength: number, height: string, width?: string, divCount: number, exponential: boolean, zIndex: number, animated: boolean | 'scroll', duration: string, easing: string, opacity: number, curve: 'linear' | 'bezier' | 'ease-in' | 'ease-out' | 'ease-in-out', responsive: boolean, mobileHeight?: string, tabletHeight?: string, desktopHeight?: string, mobileWidth?: string, tabletWidth?: string, desktopWidth?: string, preset?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'subtle'
    | 'intense'
    | 'smooth'
    | 'sharp'
    | 'header'
    | 'footer'
    | 'sidebar'
    | 'page-header'
    | 'page-footer', gpuOptimized?: boolean, hoverIntensity?: number, target: 'parent' | 'page', onAnimationComplete?: () => void, className: string, customStyle: Record<string, any> };



const DEFAULT_CONFIG: Partial<GradualBlurProps> = {
  position: 'bottom',
  strength: 2,
  height: '6rem',
  divCount: 5,
  exponential: false,
  zIndex: 1000,
  animated: false,
  duration: '0.3s',
  easing: 'ease-out',
  opacity: 1,
  curve: 'linear',
  responsive: false,
  target: 'parent',
  className: '',
  customStyle: {}
}

const PRESETS: Record<string, Partial<GradualBlurProps>> = {
  top: { position: 'top', height: '6rem' },
  bottom: { position: 'bottom', height: '6rem' },
  left: { position: 'left', height: '6rem' },
  right: { position: 'right', height: '6rem' },
  subtle: { height: '4rem', strength: 1, opacity: 0.8, divCount: 3 },
  intense: { height: '10rem', strength: 4, divCount: 8, exponential: true },
  smooth: { height: '8rem', curve: 'bezier', divCount: 10 },
  sharp: { height: '5rem', curve: 'linear', divCount: 4 },
  header: { position: 'top', height: '8rem', curve: 'ease-out' },
  footer: { position: 'bottom', height: '8rem', curve: 'ease-out' },
  sidebar: { position: 'left', height: '6rem', strength: 2.5 },
  'page-header': {
    position: 'top',
    height: '10rem',
    target: 'page',
    strength: 3
  },
  'page-footer': {
    position: 'bottom',
    height: '10rem',
    target: 'page',
    strength: 3
  }
}

const CURVE_FUNCTIONS: Record<string, (p: number) => number> = {
  linear: (p) => p,
  bezier: (p) => p * p * (3 - 2 * p),
  'ease-in': (p) => p * p,
  'ease-out': (p) => 1 - Math.pow(1 - p, 2),
  'ease-in-out': (p) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2)
}

const containerRef = ref<HTMLDivElement | null>(null)
const isHovered = ref(false)
const isVisible = ref(true)
const responsiveHeight = ref<string>()
const responsiveWidth = ref<string>()

const mergeConfigs = (...configs: Partial<GradualBlurProps>[]): Partial<GradualBlurProps> => {
  return configs.reduce((acc, config) => ({ ...acc, ...config }), {})
}

const getGradientDirection = (position: string): string => {
  const directions: Record<string, string> = {
    top: 'to top',
    bottom: 'to bottom',
    left: 'to left',
    right: 'to right'
  }
  return directions[position] || 'to bottom'
}

const debounce = <T extends (...a: any[]) => void>(fn: T, wait: number) => {
  let t: ReturnType<typeof setTimeout>
  return (...a: Parameters<T>) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...a), wait)
  }
}

const config = computed(() => {
  const presetConfig = props.preset && PRESETS[props.preset] ? PRESETS[props.preset] : {}
  return mergeConfigs(DEFAULT_CONFIG, presetConfig, props) as Required<GradualBlurProps>
})

const setIsHovered = (value: boolean) => {
  isHovered.value = value
}

const updateResponsiveDimensions = () => {
  if (!config.value.responsive) {
    responsiveHeight.value = config.value.height
    responsiveWidth.value = config.value.width
    return
  }

  const w = window.innerWidth
  let height = config.value.height
  let width = config.value.width

  // Breakpoints:
  // < 768px  => mobile
  // 768–959px => tablet
  // > 959px  => desktop
  if (w < 768) {
    if (config.value.mobileHeight) {
      height = config.value.mobileHeight
    }
    if (config.value.mobileWidth) {
      width = config.value.mobileWidth
    }
  } else if (w <= 959) {
    if (config.value.tabletHeight) {
      height = config.value.tabletHeight
    }
    if (config.value.tabletWidth) {
      width = config.value.tabletWidth
    }
  } else {
    if (config.value.desktopHeight) {
      height = config.value.desktopHeight
    }
    if (config.value.desktopWidth) {
      width = config.value.desktopWidth
    }
  }

  responsiveHeight.value = height
  responsiveWidth.value = width
}

// Performance optimization: throttled intersection observer
let intersectionTimeout: NodeJS.Timeout | null = null

const setupIntersectionObserver = () => {
  if (!config.value.animated || config.value.animated !== 'scroll' || !containerRef.value) {
    isVisible.value = true
    return
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      // Throttle intersection updates
      if (intersectionTimeout) {
        clearTimeout(intersectionTimeout)
      }

      intersectionTimeout = setTimeout(() => {
        isVisible.value = entry.isIntersecting
      }, 50) // 50ms throttle
    },
    {
      threshold: 0.1,
      rootMargin: '50px' // Start loading slightly before visible
    }
  )

  observer.observe(containerRef.value)

  return () => {
    if (intersectionTimeout) {
      clearTimeout(intersectionTimeout)
    }
    observer.disconnect()
  }
}

// Performance optimization: memoize blur calculations
const blurCache = new Map<string, Array<{ style: Record<string, any> }>>()

const blurDivs = computed(() => {
  // Create cache key for memoization
  // strength/exponentialは透明度グラデーションの見た目に使っていないためキーに含めない
  const cacheKey = `${config.value.divCount}-${config.value.position}-${config.value.curve}-${config.value.opacity}`

  // Return cached result if available
  if (blurCache.has(cacheKey)) {
    return blurCache.get(cacheKey)!
  }

  // backdrop-filterはカーソルがブラウザ外に出た際に縦線状のノイズが出る不具合があるため使わず、
  // 白の半透明グラデーションだけで「ヘッダーに近づくほど明るくなる」見た目を再現する
  const divs: Array<{ style: Record<string, any> }> = []
  const divCount = config.value.divCount
  const curveFunc = CURVE_FUNCTIONS[config.value.curve] || CURVE_FUNCTIONS.linear
  const direction = getGradientDirection(config.value.position)

  // divCountに応じた数のグラデーションストップでアルファの変化を滑らかにする
  const stops: string[] = []
  for (let i = 0; i <= divCount; i++) {
    const progress = curveFunc(i / divCount)
    const alpha = Math.min(1, Math.max(0, progress))
    const position = Math.round(((i / divCount) * 100 * 10) / 1) / 10
    stops.push(`rgba(255, 255, 255, ${alpha.toFixed(3)}) ${position}%`)
  }

  const divStyle: Record<string, any> = {
    position: 'absolute',
    inset: '0',
    background: `linear-gradient(${direction}, ${stops.join(', ')})`,
    opacity: config.value.opacity,
    transition:
      config.value.animated && config.value.animated !== 'scroll'
        ? `opacity ${config.value.duration} ${config.value.easing}`
        : undefined
  }

  divs.push({ style: divStyle })

  // Cache the result
  blurCache.set(cacheKey, divs)

  // Limit cache size to prevent memory leaks
  if (blurCache.size > 50) {
    const firstKey = blurCache.keys().next().value
    if (firstKey) {
      blurCache.delete(firstKey)
    }
  }

  return divs
})

const containerStyle = computed(() => {
  const isVertical = ['top', 'bottom'].includes(config.value.position)
  const isHorizontal = ['left', 'right'].includes(config.value.position)
  const isPageTarget = config.value.target === 'page'

  const baseStyle: Record<string, any> = {
    position: isPageTarget ? 'fixed' : 'absolute',
    pointerEvents: config.value.hoverIntensity ? 'auto' : 'none',
    opacity: isVisible.value ? 1 : 0,
    transition: config.value.animated
      ? `opacity ${config.value.duration} ${config.value.easing}`
      : undefined,
    zIndex: isPageTarget ? config.value.zIndex + 100 : config.value.zIndex,
    ...config.value.customStyle
  }

  if (isVertical) {
    baseStyle.height = responsiveHeight.value
    baseStyle.width = responsiveWidth.value || '100%'
    baseStyle[config.value.position] = 0
    baseStyle.left = 0
    baseStyle.right = 0
  } else if (isHorizontal) {
    baseStyle.width = responsiveWidth.value || responsiveHeight.value
    baseStyle.height = '100%'
    baseStyle[config.value.position] = 0
    baseStyle.top = 0
    baseStyle.bottom = 0
  }

  return baseStyle
})

const injectStyles = () => {
  if (typeof document === 'undefined') {
    return
  }
  const styleId = 'gradual-blur-styles'
  if (document.getElementById(styleId)) {
    return
  }
  const styleElement = document.createElement('style')
  styleElement.id = styleId
  styleElement.textContent =
    '.gradual-blur{pointer-events:none;transition:opacity 0.3s ease-out}.gradual-blur-inner{pointer-events:none}'
  document.head.appendChild(styleElement)
}

let resizeCleanup: (() => void) | undefined
let intersectionCleanup: (() => void) | undefined
let animationTimeout: ReturnType<typeof setTimeout> | undefined

onMounted(() => {
  // Inject styles
  injectStyles()

  // Setup responsive dimensions
  updateResponsiveDimensions()

  // Setup resize listener for responsive behavior
  if (config.value.responsive) {
    // Performance optimization: better debounced resize with passive listener
    const debouncedResize = debounce(updateResponsiveDimensions, 150)
    window.addEventListener('resize', debouncedResize, { passive: true })
    resizeCleanup = () => window.removeEventListener('resize', debouncedResize)
  }

  // Setup intersection observer
  nextTick(() => {
    intersectionCleanup = setupIntersectionObserver()
  })
})

onUnmounted(() => {
  if (resizeCleanup) {
    resizeCleanup()
  }
  if (intersectionCleanup) {
    intersectionCleanup()
  }
  if (animationTimeout) {
    clearTimeout(animationTimeout)
  }
})

// Watch for animation completion
watch(
  () => [
    isVisible.value,
    config.value.animated,
    config.value.onAnimationComplete,
    config.value.duration
  ],
  ([visible, animated, onComplete, duration]) => {
    if (animationTimeout) {
      clearTimeout(animationTimeout)
    }
    if (visible && animated === 'scroll' && onComplete) {
      const callback = onComplete as () => void
      animationTimeout = setTimeout(() => callback(), parseFloat(duration as string) * 1000)
    }
  }
)

// Watch for config changes that require responsive update
watch(
  () => config.value.responsive,
  () => {
    updateResponsiveDimensions()
  }
)

return { __sfc: true,props, DEFAULT_CONFIG, PRESETS, CURVE_FUNCTIONS, containerRef, isHovered, isVisible, responsiveHeight, responsiveWidth, mergeConfigs, getGradientDirection, debounce, config, setIsHovered, updateResponsiveDimensions, intersectionTimeout, setupIntersectionObserver, blurCache, blurDivs, containerStyle, injectStyles, resizeCleanup, intersectionCleanup, animationTimeout }
}

})